import { useState } from 'react';
import { FAQ_ITEMS } from '@/content/features';

/**
 * FAQItem Component
 * Individual FAQ accordion item
 */
function FAQItem({ faq, defaultOpen = false }: { faq: typeof FAQ_ITEMS[number]; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 shadow-soft overflow-hidden">
      {/* Question - Clickable */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
        aria-expanded={isOpen}
        aria-controls={`faq-${faq.id}`}
      >
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 pr-4">
          {faq.question}
        </h3>

        {/* Plus/Minus icon */}
        <svg
          className={`w-5 h-5 text-primary-500 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-45' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* Answer - Expandable */}
      <div
        id={`faq-${faq.id}`}
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="px-6 pb-4 pt-2 border-t border-secondary-200 dark:border-secondary-700">
          <p className="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * FeaturesFAQ Component
 * Displays frequently asked questions in accordion format
 */
export default function FeaturesFAQ() {
  return (
    <section className="py-16 md:py-24 bg-white dark:bg-secondary-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-secondary-50 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-secondary-700 dark:text-secondary-300">
            Common questions about Notematic and how it supports your clinical practice.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {FAQ_ITEMS.map((faq, index) => (
            <FAQItem key={faq.id} faq={faq} defaultOpen={index === 0} />
          ))}
        </div>
      </div>
    </section>
  );
}

